import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useSEO } from '../../shared/hooks/useSEO';
import { SEO_CONTENT } from '../../shared/constants/seo';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  SITE_URL,
  localizedPath,
  stripLanguage,
} from '../../shared/constants/routes';

interface SEOProps {
  page: keyof typeof SEO_CONTENT.en;
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string;
}

export const SEO: React.FC<SEOProps> = ({
  page,
  customTitle,
  customDescription,
  customKeywords
}) => {
  const { language } = useLanguage();
  const location = useLocation();

  // Canonical URLs always use the production origin so that preview deploys
  // never point search engines or scrapers at themselves.
  const { path } = stripLanguage(location.pathname);
  const currentUrl = `${SITE_URL}${localizedPath(path, language)}`;

  const alternates = [
    ...LANGUAGES.map((lang) => ({
      hreflang: lang,
      href: `${SITE_URL}${localizedPath(path, lang)}`,
    })),
    // x-default points at the default language so every page in the hreflang
    // set stays self-canonical. Keep in sync with scripts/prerender.js.
    { hreflang: 'x-default', href: `${SITE_URL}${localizedPath(path, DEFAULT_LANGUAGE)}` },
  ];

  const seoData = SEO_CONTENT[language as keyof typeof SEO_CONTENT][page];

  const title = customTitle || seoData.title;
  const description = customDescription || seoData.description;
  const keywords = customKeywords || seoData.keywords;

  useSEO({
    title,
    description,
    keywords,
    url: currentUrl,
    type: seoData.type,
    language: language as 'en' | 'de',
    image: `${SITE_URL}/og-image.png`,
    alternates,
  });

  return null; // This component doesn't render anything
};
